import { useState, useMemo, useEffect } from 'react';
import { useAccount, useReadContract } from 'wagmi';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { parseUnits } from 'viem';
import { Button } from '../components/ui/Button';
import { Card, StatCard, Badge } from '../components/ui/Card';
import { Input, FormField } from '../components/ui/Input';
import { useBuyFlow, useTxReceipt } from '../hooks/useBuyFlow';
import { usePresaleStats } from '../hooks/usePresaleStats';
import {
  PRESALE_ABI,
  PRESALE_ADDRESS,
  AIML_TOKEN_ADDRESS,
  USDT_DECIMALS,
} from '../lib/contracts';
import { fmtAiml, fmtUsdt } from '../lib/format';
import { useToast } from '../contexts/ToastContext';

const BLOCKAID_BANNER_DISMISSED_KEY = 'altcoins.blockaidBannerDismissed';
const BUY_URL = 'https://altcoinsmoney.com/buy';

// =====================================================================
// Wallet deep-link configurations.
//
// MetaMask:    https://metamask.app.link/dapp/<dapp-url>
//   This is a verified Android App Link / iOS Universal Link claimed by
//   MetaMask. Scanning a QR with this URL with the phone camera forces
//   MetaMask to open and load the URL inside its in-app browser.
//
// Trust Wallet: https://link.trustwallet.com/open_url?coin_id=60&url=<url>
//   The `coin_id=60` parameter is REQUIRED — without it, Trust Wallet's
//   Universal Link router doesn't reliably claim the URL and Android falls
//   back to Chrome. 60 is Ethereum's BIP-44 ID and Trust Wallet uses it as
//   the "EVM-compatible" identifier; once inside Trust Wallet's DApp
//   browser the user can switch to Polygon (or it auto-switches when wagmi
//   requests it).
// =====================================================================
const WALLETS = {
  metamask: {
    key: 'metamask',
    name: 'MetaMask',
    emoji: '🦊',
    deepLink: 'https://metamask.app.link/dapp/altcoinsmoney.com/buy',
    instructions: [
      'Open your phone\'s **camera app** (not MetaMask\'s scanner)',
      'Point it at the QR code on screen',
      'Tap the notification — MetaMask opens automatically',
      'The buy page loads inside MetaMask\'s browser',
      'Enter your USDT and tap Buy — confirms inline',
    ],
  },
  trustwallet: {
    key: 'trustwallet',
    name: 'Trust Wallet',
    emoji: '🛡️',
    // coin_id=60 is critical — see comment block above.
    deepLink: `https://link.trustwallet.com/open_url?coin_id=60&url=${encodeURIComponent(BUY_URL)}`,
    instructions: [
      'Open your phone\'s **camera app** (not Trust Wallet\'s scanner)',
      'Point it at the QR code on screen',
      'Tap the notification — Trust Wallet opens automatically',
      'The buy page loads inside Trust Wallet\'s DApp browser',
      'Enter your USDT and tap Buy — confirms inline',
    ],
  },
} as const;

type WalletKey = keyof typeof WALLETS;

function qrSrcFor(deepLink: string): string {
  // QR served by api.qrserver.com — caches well on their CDN.
  return `https://api.qrserver.com/v1/create-qr-code/?size=240x240&margin=8&data=${encodeURIComponent(deepLink)}`;
}

function detectMobile(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

// =====================================================================
// QR Modal — QR-first layout. Wallet tabs at top, big QR centred,
// scan steps below. URL-paste fallback for non-MM/non-TW wallets sits
// in a smaller footer block.
// =====================================================================
function QrModal({
  open,
  onClose,
  isMobile,
}: {
  open: boolean;
  onClose: () => void;
  isMobile: boolean;
}) {
  const [activeWallet, setActiveWallet] = useState<WalletKey>('metamask');
  const [urlCopied, setUrlCopied] = useState(false);
  const wallet = WALLETS[activeWallet];
  const qrSrc = qrSrcFor(wallet.deepLink);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  const copyUrl = () => {
    if (typeof navigator === 'undefined' || !navigator.clipboard) return;
    navigator.clipboard.writeText(BUY_URL).then(() => {
      setUrlCopied(true);
      setTimeout(() => setUrlCopied(false), 2000);
    });
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-5 sm:p-6 relative max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 text-ink-600 hover:text-ink text-2xl leading-none w-10 h-10 rounded-full hover:bg-paper-200 flex items-center justify-center"
        >
          ×
        </button>

        <div className="font-display font-bold text-xl text-ink mb-1 pr-10">
          📱 Buy with QR Code
        </div>
        <p className="text-sm text-ink-700 mb-4">
          Scan with your phone camera to open this page directly in your wallet's
          built-in browser — smoothest checkout, no popups.
        </p>

        {/* ============================================================
            Wallet selector tabs
            ============================================================ */}
        <div className="grid grid-cols-2 gap-2 mb-5 p-1 bg-paper-100 rounded-2xl">
          {(Object.keys(WALLETS) as WalletKey[]).map((key) => {
            const w = WALLETS[key];
            const active = key === activeWallet;
            return (
              <button
                key={key}
                onClick={() => setActiveWallet(key)}
                className={`flex items-center justify-center gap-2 py-2.5 rounded-xl font-semibold text-sm transition ${
                  active
                    ? 'bg-white text-ink shadow-sm'
                    : 'text-ink-600 hover:text-ink'
                }`}
              >
                <span className="text-lg">{w.emoji}</span>
                {w.name}
              </button>
            );
          })}
        </div>

        {/* ============================================================
            QR Code — the star of the show
            ============================================================ */}
        <div className="flex flex-col items-center gap-3 mb-5">
          <div className="p-4 bg-white rounded-2xl border-2 border-violet-500/30 shadow-sm">
            <img
              key={wallet.key /* force re-render on tab switch so loading state shows */}
              src={qrSrc}
              alt={`Scan to open in ${wallet.name}`}
              width={240}
              height={240}
              className="block"
              loading="lazy"
            />
          </div>
          <div className="flex items-center gap-2 text-sm text-ink-700">
            <span className="text-lg">{wallet.emoji}</span>
            <span>Encoded for <strong>{wallet.name}</strong></span>
          </div>
        </div>

        {/* ============================================================
            Step-by-step scan instructions
            ============================================================ */}
        <div className="mb-5 p-4 bg-paper-100 rounded-2xl">
          <div className="text-xs font-bold text-ink uppercase tracking-wide mb-3">
            How to scan
          </div>
          <ol className="space-y-2 text-sm text-ink-700">
            {wallet.instructions.map((step, i) => (
              <li key={i} className="flex gap-2.5">
                <span className="shrink-0 w-5 h-5 rounded-full bg-violet-500 text-white font-bold text-[11px] flex items-center justify-center mt-0.5">
                  {i + 1}
                </span>
                <span
                  className="flex-1"
                  dangerouslySetInnerHTML={{
                    __html: step.replace(
                      /\*\*(.+?)\*\*/g,
                      '<strong class="text-ink">$1</strong>',
                    ),
                  }}
                />
              </li>
            ))}
          </ol>
        </div>

        {/* ============================================================
            Mobile direct-open shortcut — for users on their phone now
            ============================================================ */}
        {isMobile && (
          <a
            href={wallet.deepLink}
            className="flex items-center justify-center gap-2 p-3 rounded-xl bg-gradient-to-r from-violet-500 to-magenta text-white font-semibold text-sm mb-4"
          >
            <span className="text-xl">{wallet.emoji}</span>
            On mobile? Tap to open {wallet.name} directly
          </a>
        )}

        {/* ============================================================
            Footer — other wallets get the URL paste fallback
            ============================================================ */}
        <div className="pt-4 border-t border-paper-300">
          <div className="text-xs font-semibold text-ink-700 mb-2">
            Using OKX, Bitget, Binance, or another wallet?
          </div>
          <p className="text-xs text-ink-600 mb-3">
            Open your wallet's built-in <strong>Browser / DApps</strong> tab and paste
            this URL in the search bar:
          </p>
          <div className="bg-paper-100 p-2.5 rounded-xl flex items-center gap-2">
            <code className="flex-1 text-xs text-ink break-all">{BUY_URL}</code>
            <button
              onClick={copyUrl}
              className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                urlCopied
                  ? 'bg-success text-white'
                  : 'bg-violet-500 text-white hover:bg-violet-600'
              }`}
            >
              {urlCopied ? '✓ Copied' : '📋 Copy'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// =====================================================================
// Token Visibility Guide — unchanged
// =====================================================================
function TokenVisibilityGuide({
  defaultOpen = false,
  highlighted = false,
}: {
  defaultOpen?: boolean;
  highlighted?: boolean;
}) {
  const [expanded, setExpanded] = useState(defaultOpen);
  const [copied, setCopied] = useState(false);

  const copyAddress = () => {
    if (typeof navigator === 'undefined' || !navigator.clipboard) return;
    navigator.clipboard.writeText(AIML_TOKEN_ADDRESS).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const wrapClass = highlighted
    ? 'p-5 rounded-2xl bg-gradient-to-br from-violet-500/5 to-magenta/5 border border-violet-500/30'
    : 'p-5 rounded-2xl bg-paper-100 border border-paper-300';

  return (
    <div className={wrapClass}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between text-left gap-3"
      >
        <div>
          <div className="font-display font-semibold text-ink text-base sm:text-lg">
            👀 How to see your AIML tokens in your wallet
          </div>
          <p className="text-xs sm:text-sm text-ink-600 mt-1">
            After buying, you need to add AIML as a custom token to see the balance.
          </p>
        </div>
        <span className="text-2xl text-ink-600 shrink-0 w-8 h-8 flex items-center justify-center">
          {expanded ? '−' : '+'}
        </span>
      </button>

      {expanded && (
        <div className="mt-5 pt-5 border-t border-paper-300">
          <div className="bg-white p-4 rounded-xl mb-5 border border-paper-300">
            <div className="text-xs text-ink-600 uppercase tracking-wide mb-2 font-semibold">
              AIML Token Contract Address (Polygon)
            </div>
            <div className="font-mono text-xs sm:text-sm break-all mb-3 text-ink">
              {AIML_TOKEN_ADDRESS}
            </div>
            <button
              onClick={copyAddress}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full ${
                copied
                  ? 'bg-success/10 text-success'
                  : 'bg-violet-500/10 text-violet-600 hover:bg-violet-500/20'
              }`}
            >
              {copied ? '✓ Copied!' : '📋 Copy address'}
            </button>
          </div>

          <ol className="space-y-4 text-sm text-ink-700">
            <li className="flex gap-3">
              <span className="shrink-0 w-7 h-7 rounded-full bg-violet-500 text-white font-bold text-xs flex items-center justify-center">1</span>
              <div>
                <strong className="text-ink">Open your wallet (MetaMask or Trust Wallet).</strong>
                <div className="text-xs text-ink-600 mt-0.5">Either the mobile app or the browser extension.</div>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="shrink-0 w-7 h-7 rounded-full bg-violet-500 text-white font-bold text-xs flex items-center justify-center">2</span>
              <div>
                <strong className="text-ink">Switch to the Polygon network.</strong>
                <div className="text-xs text-ink-600 mt-0.5">
                  <strong>MetaMask:</strong> tap the network selector at the top → pick "Polygon Mainnet". If it's not in the list, tap "Add network" → search "Polygon" → Add.
                </div>
                <div className="text-xs text-ink-600 mt-1">
                  <strong>Trust Wallet:</strong> Polygon shows automatically — no switching needed.
                </div>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="shrink-0 w-7 h-7 rounded-full bg-violet-500 text-white font-bold text-xs flex items-center justify-center">3</span>
              <div>
                <strong className="text-ink">Find "Import token" or "Add custom token".</strong>
                <div className="text-xs text-ink-600 mt-0.5">
                  <strong>MetaMask:</strong> scroll to the bottom of your tokens list and tap "Import tokens".
                </div>
                <div className="text-xs text-ink-600 mt-1">
                  <strong>Trust Wallet:</strong> tap the settings ⚙️ at top right of the tokens list → "Manage tokens" → "+" icon, or tap "Add Custom Token".
                </div>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="shrink-0 w-7 h-7 rounded-full bg-violet-500 text-white font-bold text-xs flex items-center justify-center">4</span>
              <div>
                <strong className="text-ink">Paste the AIML contract address (from above).</strong>
                <div className="text-xs text-ink-600 mt-0.5">
                  Make sure the network is set to Polygon. The wallet should auto-fill the symbol (<strong>AIML</strong>) and decimals (<strong>18</strong>). If not, enter them manually.
                </div>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="shrink-0 w-7 h-7 rounded-full bg-violet-500 text-white font-bold text-xs flex items-center justify-center">5</span>
              <div>
                <strong className="text-ink">Confirm — your AIML balance appears.</strong>
                <div className="text-xs text-ink-600 mt-0.5">
                  You'll see your full purchased amount. (e.g. buying with 5 USDT shows about 42,735 AIML.)
                </div>
              </div>
            </li>
          </ol>

          <div className="mt-5 p-3 rounded-xl bg-warn/5 border border-warn/20 text-xs text-ink-700">
            ⚠️ <strong>Vesting reminder:</strong> AIML tokens unlock linearly over 6 months starting from TGE (Token Generation Event). The number you see in your wallet is your <em>total allocation</em>, including bonuses — tokens become transferable gradually.
          </div>
        </div>
      )}
    </div>
  );
}

// =====================================================================
// Main Buy page
// =====================================================================
export default function Buy() {
  const { address, isConnected } = useAccount();
  const [usdt, setUsdt] = useState('100');
  const presale = usePresaleStats();
  const flow = useBuyFlow();
  const receipt = useTxReceipt(flow.txHash ?? undefined);
  const toast = useToast();

  const referralCode =
    typeof window !== 'undefined' ? localStorage.getItem('altcoins.refCode') ?? '' : '';

  const usdtNum = parseFloat(usdt) || 0;
  const isMobile = detectMobile();

  const [qrModalOpen, setQrModalOpen] = useState(false);

  const [showBlockaidBanner, setShowBlockaidBanner] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    setShowBlockaidBanner(
      localStorage.getItem(BLOCKAID_BANNER_DISMISSED_KEY) !== 'true',
    );
  }, []);
  const dismissBlockaidBanner = () => {
    localStorage.setItem(BLOCKAID_BANNER_DISMISSED_KEY, 'true');
    setShowBlockaidBanner(false);
  };

  const usdtRaw = useMemo(() => {
    try {
      if (!usdt || usdtNum <= 0) return 0n;
      return parseUnits(usdt, USDT_DECIMALS);
    } catch {
      return 0n;
    }
  }, [usdt, usdtNum]);

  const quote = useReadContract({
    address: PRESALE_ADDRESS,
    abi: PRESALE_ABI,
    functionName: 'getTokenAmountUSDT',
    args: usdtRaw > 0n ? [usdtRaw] : undefined,
    query: { enabled: usdtRaw > 0n },
  });

  const tokensOut = useMemo(() => {
    if (quote.data === undefined) return 0;
    return Number(quote.data as bigint);
  }, [quote.data]);

  useEffect(() => {
    if (receipt.data && flow.txHash && flow.step !== 'done' && flow.step !== 'error') {
      toast.success('Purchase confirmed on-chain!');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receipt.data, flow.txHash]);

  const canBuy = usdtNum > 0;
  const isWorking =
    flow.step === 'approving' ||
    flow.step === 'approve-confirming' ||
    flow.step === 'buying' ||
    flow.step === 'buy-confirming' ||
    flow.step === 'attributing' ||
    flow.step === 'switching-chain';

  async function handleBuy() {
    if (!isConnected || !address) {
      toast.error('Connect a wallet first');
      return;
    }
    if (presale.paused) {
      toast.error('Sales are paused right now. Please try later.');
      return;
    }
    await flow.buy(usdt, referralCode || undefined);
  }

  const BlockaidNotice = () =>
    showBlockaidBanner ? (
      <div className="max-w-2xl mx-auto mb-6 p-4 rounded-2xl bg-paper-100 border border-paper-300 flex items-start gap-3">
        <span className="text-xl shrink-0">🛡️</span>
        <div className="flex-1 text-sm">
          <div className="font-display font-semibold text-ink mb-1">
            About wallet security warnings
          </div>
          <p className="text-ink-700 leading-relaxed">
            Your wallet may show a warning when approving — this is automatic for
            recently deployed contracts and is <strong>not specific to us</strong>.
            Our contract is{' '}
            <a href="/AIML-Audit.pdf" target="_blank" rel="noopener noreferrer" className="text-violet-600 underline">audited</a>{' '}
            and{' '}
            <a href={`https://polygonscan.com/address/${PRESALE_ADDRESS}#code`} target="_blank" rel="noopener noreferrer" className="text-violet-600 underline">
              verified on Polygonscan
            </a>. You can safely confirm.
          </p>
        </div>
        <button onClick={dismissBlockaidBanner} aria-label="Dismiss" className="text-ink-600 hover:text-ink text-lg leading-none">×</button>
      </div>
    ) : null;

  const WrongChainBanner = () =>
    flow.wrongChain ? (
      <div className="mb-4 p-4 rounded-2xl bg-warn/5 border border-warn/30">
        <div className="font-display font-semibold text-warn mb-1">Wrong network</div>
        <p className="text-sm text-ink-700 mb-3">
          Your wallet is on a different network. AIML is on <strong>Polygon Mainnet</strong>.
        </p>
        <Button size="md" onClick={() => flow.switchToPolygon()} isLoading={flow.step === 'switching-chain'}>
          Switch to Polygon
        </Button>
      </div>
    ) : null;

  const StuckRecovery = () =>
    flow.stuck && (flow.step === 'approving' || flow.step === 'buying') ? (
      <div className="mt-4 p-4 rounded-2xl bg-warn/5 border border-warn/30">
        <div className="font-display font-semibold text-warn mb-1">Wallet not responding?</div>
        <p className="text-sm text-ink-700 mb-3">
          {flow.isMobile
            ? 'Open your wallet app manually and look for a pending request. If your wallet isn\'t showing the request, try the QR Code button at the top to open this page inside your wallet\'s browser — much more reliable on mobile.'
            : 'Check your wallet pop-up or browser extension for a pending request.'}
        </p>
        <div className="flex flex-wrap gap-2">
          {flow.txHash && (
            <a href={`https://polygonscan.com/tx/${flow.txHash}`} target="_blank" rel="noopener noreferrer" className="text-sm px-4 py-2 rounded-full bg-paper-300 hover:bg-paper-400 text-ink font-semibold">
              View tx on Polygonscan
            </a>
          )}
          <button onClick={flow.reset} className="text-sm px-4 py-2 rounded-full bg-danger/10 hover:bg-danger/20 text-danger font-semibold">
            Cancel and reset
          </button>
        </div>
      </div>
    ) : null;

  const SuccessCard = () =>
    flow.step === 'done' && flow.txHash ? (
      <div className="mt-4 p-5 rounded-2xl bg-success/5 border border-success/20">
        <div className="font-display text-xl font-bold text-success mb-1">✓ Purchase successful</div>
        <p className="text-sm text-ink-700 mb-3">
          Your AIML tokens are on the way to your wallet. Follow the guide below to see them.
        </p>
        <div className="text-xs font-mono text-ink-600 mb-2 break-all">Tx: {flow.txHash}</div>
        <div className="flex flex-wrap gap-3 items-center mb-5">
          <a href={`https://polygonscan.com/tx/${flow.txHash}`} target="_blank" rel="noopener noreferrer" className="text-sm text-violet-600 underline font-semibold">
            View on Polygonscan →
          </a>
          {receipt.data && (
            <span className="text-xs text-success">· Confirmed in block {Number(receipt.data.blockNumber)}</span>
          )}
          <button onClick={flow.reset} className="ml-auto text-xs text-ink-600 underline">Buy again</button>
        </div>
        <TokenVisibilityGuide defaultOpen={true} highlighted={true} />
      </div>
    ) : null;

  // =====================================================================

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <div className="text-center mb-10">
        <div className="eyebrow mb-3">PRESALE BUY</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">
          Buy <span className="text-gradient">$AIML</span> with USDT
        </h1>
        <p className="mt-3 text-ink-600">
          Polygon mainnet. Connect your wallet, approve once, buy in seconds.
        </p>

        <div className="mt-5">
          <button
            onClick={() => setQrModalOpen(true)}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-violet-500 to-magenta text-white font-display font-semibold shadow-lg hover:shadow-xl transition-all hover:scale-105"
          >
            <span className="text-lg">📱</span>
            <span>Buy with QR Code</span>
          </button>
          <p className="text-xs text-ink-600 mt-2">
            Scan with phone camera → opens inside MetaMask or Trust Wallet for smoothest checkout
          </p>
        </div>
      </div>

      <BlockaidNotice />

      {presale.paused && (
        <div className="max-w-2xl mx-auto mb-8 p-4 rounded-2xl bg-warn/5 border border-warn/30 text-center">
          <div className="font-display font-semibold text-warn">Presale paused</div>
          <p className="text-sm text-ink-700 mt-1">Token sales are currently paused. Check back soon.</p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <StatCard label="RAISED" value={presale.totalUsdtRaised !== null ? fmtUsdt(presale.totalUsdtRaised) : '—'} accent="violet" />
        <StatCard label="RATE" value={presale.tokensPerUsdt !== null ? fmtAiml(presale.tokensPerUsdt) : '—'} hint="AIML / USDT" accent="magenta" />
        <StatCard label="SOLD" value={presale.totalAimlSold !== null ? fmtAiml(presale.totalAimlSold) : '—'} hint="AIML" accent="azure" />
      </div>

      {presale.error && (
        <div className="mb-6 p-4 rounded-2xl bg-danger/5 border border-danger/30">
          <div className="font-display font-semibold text-danger mb-1">Couldn't read contract data</div>
          <div className="text-xs font-mono text-ink-700 break-all">
            {(presale.error as Error).message || String(presale.error)}
          </div>
        </div>
      )}

      <Card className="!p-8">
        {referralCode && (
          <div className="mb-6 p-3 rounded-2xl bg-violet-500/5 border border-violet-500/20 flex items-center justify-between">
            <div className="text-sm">
              <span className="font-mono text-violet-600 font-semibold">{referralCode}</span>
              <span className="text-ink-600 ml-2">applied as your referrer</span>
            </div>
            <Badge kind="info">REF</Badge>
          </div>
        )}

        <WrongChainBanner />

        <FormField
          label="Amount in USDT"
          hint={
            quote.isLoading
              ? 'Fetching quote from contract…'
              : tokensOut > 0
                ? `You'll receive ≈ ${fmtAiml(tokensOut)} AIML`
                : 'Enter any positive amount in USDT'
          }
        >
          <div className="relative">
            <Input
              type="number"
              inputMode="decimal"
              min="0"
              step="any"
              value={usdt}
              onChange={(e) => setUsdt(e.target.value)}
              placeholder="100"
              className="!h-16 !text-2xl !pr-24 font-display"
            />
            <div className="absolute right-4 top-1/2 -translate-y-1/2 font-mono font-semibold text-ink-600">USDT</div>
          </div>
        </FormField>

        <div className="mt-6 p-5 rounded-2xl bg-paper-200 flex items-center justify-between">
          <div>
            <div className="eyebrow mb-1">YOU RECEIVE</div>
            <div className="font-display text-3xl font-bold text-gradient tabular-nums">{fmtAiml(tokensOut)}</div>
          </div>
          <div className="text-right">
            <div className="text-xs text-ink-600 font-mono">AIML</div>
            <div className="text-xs text-ink-600 mt-1">live quote</div>
          </div>
        </div>

        <div className="mt-4 p-3 rounded-2xl bg-violet-500/5 border border-violet-500/10 text-xs text-ink-700">
          💡 Buy $150+ to become a <strong>Bronze Affiliate</strong> · $300+ to upgrade to <strong>Silver</strong>. Unlocks your referral link and 15–20% commission on every friend you bring.
        </div>

        {flow.isMobile && isWorking && (
          <div className="mt-4 p-3 rounded-2xl bg-azure-500/5 border border-azure-500/20 text-xs text-ink-700">
            📱 <strong>Stay on this page.</strong> Open your wallet app to confirm the request, then come back — don't close this tab.
          </div>
        )}

        <div className="mt-6">
          {!isConnected ? (
            <ConnectButton.Custom>
              {({ openConnectModal }) => (
                <Button size="lg" className="w-full" onClick={openConnectModal}>Connect wallet to buy</Button>
              )}
            </ConnectButton.Custom>
          ) : flow.wrongChain ? (
            <Button size="lg" className="w-full" onClick={() => flow.switchToPolygon()} isLoading={flow.step === 'switching-chain'}>
              Switch to Polygon
            </Button>
          ) : (
            <Button
              size="lg"
              className="w-full"
              onClick={handleBuy}
              isLoading={isWorking}
              disabled={!canBuy || isWorking || presale.paused}
            >
              {flowLabel(flow.step) || (canBuy ? `Buy ${fmtAiml(tokensOut)} AIML for ${fmtUsdt(usdtNum)}` : 'Enter an amount')}
            </Button>
          )}
        </div>

        <StuckRecovery />

        {flow.errorMessage && (
          <div className="mt-4 p-3 rounded-2xl bg-danger/5 border border-danger/20 text-danger text-sm flex items-start justify-between gap-3">
            <span className="flex-1">{flow.errorMessage}</span>
            <button onClick={flow.reset} className="text-danger underline shrink-0">Reset</button>
          </div>
        )}

        {flow.txHash && flow.step !== 'done' && (
          <div className="mt-4 p-3 rounded-2xl bg-paper-200 text-xs">
            <span className="text-ink-600">Tx submitted: </span>
            <a href={`https://polygonscan.com/tx/${flow.txHash}`} target="_blank" rel="noopener noreferrer" className="font-mono text-violet-600 underline break-all">
              {flow.txHash.slice(0, 18)}…{flow.txHash.slice(-6)}
            </a>
          </div>
        )}

        <SuccessCard />
      </Card>

      <div className="mt-8">
        <TokenVisibilityGuide defaultOpen={false} highlighted={false} />
      </div>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
        <div className="card-base p-5">
          <div className="font-display font-semibold text-ink mb-1">USDT goes direct</div>
          <p className="text-ink-600 text-xs">Your USDT transfers to the project's ops wallet on Polygon — verifiable on Polygonscan.</p>
        </div>
        <div className="card-base p-5">
          <div className="font-display font-semibold text-ink mb-1">Affiliate program</div>
          <p className="text-ink-600 text-xs">Buy $150+ to unlock your referral link. Earn 15-22% USDT cash + AIML bonus on every friend's purchase.</p>
        </div>
        <div className="card-base p-5">
          <div className="font-display font-semibold text-ink mb-1">Vesting starts at TGE</div>
          <p className="text-ink-600 text-xs">AIML tokens unlock linearly over six months from token generation event, including bonuses.</p>
        </div>
      </div>

      <QrModal open={qrModalOpen} onClose={() => setQrModalOpen(false)} isMobile={isMobile} />
    </div>
  );
}

function flowLabel(step: string): string | null {
  switch (step) {
    case 'switching-chain': return 'Switching to Polygon…';
    case 'approving': return 'Approve USDT in your wallet…';
    case 'approve-confirming': return 'Waiting for approval confirmation…';
    case 'buying': return 'Confirm purchase in your wallet…';
    case 'buy-confirming': return 'Waiting for buy confirmation…';
    case 'attributing': return 'Recording attribution…';
    default: return null;
  }
}
